import { useSeo } from '../lib/seo';
/**
 * /services — B2B landing page for LPL Tourism's consultancy work.
 *
 * This is the destination for the eventual redirect from lpltourism.com. The
 * consumer-facing /about page stays as-is (public credibility); /services is
 * where operators, DMOs and public-sector partners land to learn what LPL sells.
 *
 * Content is hard-coded (editable in this file). Team portraits are stored in
 * `site_settings.team_photos` so the admin can swap them without a redeploy.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, BarChart3, Briefcase, Calendar, ClipboardList,
  Cog, Compass, Globe2, HeartHandshake, Lightbulb, Mail,
  Megaphone, Rocket, Sparkles, Users,
} from 'lucide-react';

const SERVICES = [
  {
    icon: Sparkles,
    title: 'Short break packaging',
    body: 'Creating, marketing and selling holiday & short break packages is our specialist skill — 30+ years of live experience running Stay Play Explore. We help other destinations do the same, end-to-end.',
    featured: true,
  },
  {
    icon: Compass,
    title: 'Destination strategy & masterplanning',
    body: 'Long-view strategy for local authorities, DMOs and BIDs. We help destinations articulate a vision, prioritise investment, and set five- to ten-year plans grounded in visitor data and stakeholder consensus.',
  },
  {
    icon: Megaphone,
    title: 'Strategic marketing',
    body: 'Marketing plans that connect destination purpose to measurable outcomes — audience insight, positioning, campaign architecture, and channel mix, with clear KPIs and reporting.',
  },
  {
    icon: Sparkles,
    title: 'Marketing campaigns & delivery',
    body: 'From brief to booking, we run integrated seasonal and always-on campaigns — creative, PR, paid social, influencer partnerships, and content — with in-house delivery capability.',
  },
  {
    icon: Globe2,
    title: 'Website & digital development',
    body: 'Consumer-facing destination websites, content management systems and integrated visitor tools. LPL builds and runs GoLeicestershire and StayPlayExplore end-to-end.',
  },
  {
    icon: Calendar,
    title: 'Event & festival delivery',
    body: 'From concept through operations. LPL has delivered tourism awards, product launches, familiarisation trips and multi-day festivals across the East Midlands and beyond.',
  },
  {
    icon: HeartHandshake,
    title: 'Trade partnerships & DMO management',
    body: 'Building networks of visitor economy operators, running membership programmes, and providing the ongoing management structure a modern DMO needs to hold itself together.',
  },
  {
    icon: Lightbulb,
    title: 'Consultancy & management development',
    body: 'Retained advisory work for boards, senior teams, and public-sector partners. Mentoring, board development, governance reviews and interim leadership.',
  },
  {
    icon: Rocket,
    title: 'Business diversification & growth',
    body: 'Where a tourism-adjacent business needs a new revenue stream — retail, corporate, weddings, education — we bring product-development thinking and route-to-market expertise.',
  },
  {
    icon: Cog,
    title: 'Systems & process guidance',
    body: 'Operational reviews of visitor-facing systems: booking, CRM, data flow, integrations and reporting. Practical recommendations, not consultancy PDFs that sit on a shelf.',
  },
];

const TEAM = [
  {
    key: 'martin',
    name: 'Martin Peters',
    role: 'Chief Executive',
    bio: `Martin has more than 30 years' experience in destination management, tourism, place marketing and organisational leadership. Throughout his career he has worked with local authorities, businesses, destination organisations and government agencies to develop strategies, build partnerships and deliver practical solutions that achieve measurable results.

His previous national roles include Chairman of the British Association of Conference Destinations, founding member of the British Cities Marketing Board, and Vice-Chair of VisitEngland's Destination Management Forum. He has also contributed to a number of national working groups and think tanks examining tourism funding, destination management and Tourism Business Improvement Districts (TBIDs), and has led and supported multiple area tourism partnerships. Martin has been a Fellow of the Tourism Society since 1997.

Martin is particularly recognised for his strategic thinking and his ability to bring clarity to complex issues. He has a strong track record of helping organisations define priorities, develop practical strategies and turn ambitious ideas into deliverable plans. He is known for making complex projects easy to understand, enabling boards, directors and senior leadership teams to make confident decisions. Alongside organisational strategy, he provides mentoring, business support and trusted advice to directors, senior managers and business leaders, helping them navigate change, manage challenging projects and achieve sustainable growth.`,
  },
  {
    key: 'janine',
    name: 'Janine Williams',
    role: 'Deputy Chief Executive & Director of Marketing',
    bio: `20+ years in tourism and marketing, with a track record of designing and delivering outcome-driven tactical marketing campaigns — from destination-wide festivals to bespoke commercial projects — where success is measured in bookings, visits and revenue rather than impressions. Her philosophy is simple: clicks and likes are vanity, bookings are sanity. Multiple CIM qualifications; has sat on numerous VisitEngland forums and currently sits on the VisitEngland Tourism Awards advisory board.`,
  },
];

const DESTINATIONS = [
  'Leicester & Leicestershire', 'Lincolnshire', 'Rutland', 'Yorkshire',
  'Stratford-upon-Avon', 'Warwickshire', 'Coventry', 'Staffordshire',
  'Birmingham', 'Northamptonshire',
];


export default function ServicesPage() {
  useSeo(
    'LPL Tourism Consultancy Services',
    'Destination marketing, tourism strategy, venue promotion and visitor-economy consultancy from LPL — the team behind Go Leicestershire. Practical, proven support that grows footfall, bookings and awareness for venues and destinations.'
  );

  return (
    <div className="bg-base">
      <Helmet>
        <title>Tourism industry services — LPL Tourism · GoLeicestershire</title>
        <meta name="description" content="LPL Tourism helps destinations, DMOs and visitor economy operators grow — with strategy, marketing, events, digital and consultancy." />
      </Helmet>

      {/* Hero */}
      <section className="bg-heritage text-base relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40"/>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20 md:py-28 relative">
          <div className="label text-mustard mb-4 flex items-center gap-2"><Briefcase size={14}/> LPL Tourism specialists</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
            Building better <em className="not-italic text-mustard">destinations</em> together.
          </h1>
          <p className="text-lg md:text-xl text-base/85 max-w-3xl leading-relaxed">
            LPL Tourism is a specialist consultancy delivering strategy, marketing, events and digital services for
            destinations, DMOs and visitor economy operators. We&apos;ve done the day-job for 30+ years —
            our advice comes from having actually run the campaigns, the systems and the partnerships we recommend.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#services" className="inline-flex items-center gap-2 bg-mustard text-charcoal px-5 py-3 rounded-full font-semibold hover:bg-base transition-colors">
              What we do <ArrowRight size={14}/>
            </a>
            <a href="mailto:martin.peters@l-p-l.com?subject=Consultancy%20enquiry"
              data-testid="services-hero-contact"
              className="inline-flex items-center gap-2 border border-base/40 text-base px-5 py-3 rounded-full font-semibold hover:bg-charcoal hover:border-charcoal transition-colors">
              <Mail size={14}/> Talk to us
            </a>
          </div>
        </div>
      </section>

      {/* Destinations strip */}
      <section className="bg-stone border-y border-charcoal/10 py-10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="label text-heritage mb-4 text-center">Destinations we&apos;ve worked with</div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-charcoal/75 font-display text-lg" data-testid="destinations-list">
            {DESTINATIONS.map((d, i) => (
              <span key={d} className="flex items-center gap-3">
                <span>{d}</span>
                {i < DESTINATIONS.length - 1 && <span className="text-charcoal/25">·</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section id="services" className="bg-base py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-14">
            <div className="label text-heritage mb-3">Our services</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">
              End-to-end capability — from big-picture strategy to the day-to-day delivery it takes to make it happen.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} data-testid={`service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`bg-white border rounded-2xl p-6 lg:p-7 hover:shadow-soft transition-all ${
                    s.featured
                      ? 'border-heritage/60 ring-1 ring-heritage/20 hover:border-heritage bg-heritage/[0.02]'
                      : 'border-charcoal/10 hover:border-heritage/40'
                  }`}>
                  <div className="w-11 h-11 rounded-xl bg-heritage/10 text-heritage flex items-center justify-center mb-5">
                    <Icon size={20}/>
                  </div>
                  <div className="font-display text-xl leading-tight mb-2 flex items-center gap-2">
                    {s.title}
                    {s.featured && <span className="text-[9px] uppercase tracking-widest font-bold bg-mustard/70 text-charcoal px-2 py-0.5 rounded-full">Our specialism</span>}
                  </div>
                  <p className="text-sm text-charcoal/70 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured spotlight — Short break packaging (LPL's flagship expertise) */}
      <section className="bg-charcoal text-base py-20 md:py-24 relative overflow-hidden" data-testid="services-spotlight-shortbreaks">
        <div className="absolute inset-0 grain opacity-30"/>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="label text-mustard mb-4 flex items-center gap-2"><Sparkles size={14}/> Our flagship expertise</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
                Create &amp; sell <em className="not-italic text-mustard">short break packages</em> with a team that&apos;s been doing it for 30+ years.
              </h2>
              <div className="space-y-4 text-base/85 text-lg leading-relaxed">
                <p>
                  LPL is one of the <strong className="text-base">longest-standing organisations in the country</strong> creating,
                  marketing and selling holiday and short break packages — and also one of the most successful. Our{' '}
                  <a href="https://www.stayplayexplore.co.uk" target="_blank" rel="noopener noreferrer" className="text-mustard underline">
                    Stay Play Explore
                  </a>{' '}
                  campaign is proof: <strong className="text-base">6,000+ bookings</strong> and{' '}
                  <strong className="text-base">45,000+ visitor nights</strong> delivered from a single destination platform
                  we built and run ourselves.
                </p>
                <p>
                  We&apos;re opening the doors on that expertise to <em>other destinations</em>. If your area wants an integrated short-break
                  product — properly packaged, professionally marketed, and sold through a modern booking engine — we&apos;ll bring the
                  strategic thinking, the operational muscle, and the sophisticated backend systems we&apos;ve refined over three decades.
                </p>
              </div>
              <ul className="mt-6 space-y-2 text-base/80 text-sm">
                <li className="flex items-start gap-2"><Sparkles size={14} className="text-mustard mt-1 shrink-0"/>End-to-end product design — themes, itineraries, partner sourcing, commercial terms</li>
                <li className="flex items-start gap-2"><Sparkles size={14} className="text-mustard mt-1 shrink-0"/>White-label booking engine + CRM + reporting, built specifically for destination short breaks</li>
                <li className="flex items-start gap-2"><Sparkles size={14} className="text-mustard mt-1 shrink-0"/>Consumer marketing that actually converts — email, paid, PR, influencer, and content</li>
                <li className="flex items-start gap-2"><Sparkles size={14} className="text-mustard mt-1 shrink-0"/>Partner onboarding, commissioning models, and ongoing account management</li>
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="mailto:janine.williams@l-p-l.com?subject=Short%20break%20packaging%20—%20partnering%20with%20LPL"
                  data-testid="services-spotlight-cta"
                  className="inline-flex items-center gap-2 bg-mustard text-charcoal px-5 py-3 rounded-full font-semibold hover:bg-base transition-colors">
                  <Mail size={14}/> Talk to Janine about partnering
                </a>
                <a href="https://www.stayplayexplore.co.uk" target="_blank" rel="noopener noreferrer"
                  data-testid="services-spotlight-spe"
                  className="inline-flex items-center gap-2 border border-base/40 text-base px-5 py-3 rounded-full font-semibold hover:bg-base/10 transition-colors">
                  See Stay Play Explore <ArrowRight size={14}/>
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-base/5 border border-base/10 rounded-2xl p-6 md:p-7 backdrop-blur-sm">
                <div className="label text-mustard mb-4">Our track record</div>
                <dl className="space-y-5">
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-base/55">Package bookings delivered</dt>
                    <dd className="font-display text-4xl">6,000+</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-base/55">Visitor nights delivered</dt>
                    <dd className="font-display text-4xl">45,000+</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-base/55">Destinations supported</dt>
                    <dd className="font-display text-4xl">10+</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-base/55">Years running the platform</dt>
                    <dd className="font-display text-4xl">30+</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-stone border-y border-charcoal/10 py-20 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-14">
            <div className="label text-heritage mb-3 flex items-center gap-2"><Users size={14}/> Senior leadership</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">Who you&apos;ll actually be working with.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-testid="team-grid">
            {TEAM.map((m) => (
              <div key={m.key} data-testid={`team-member-${m.key}`}>
                <div className="font-display text-2xl mb-1">{m.name}</div>
                <div className="text-heritage font-semibold text-sm mb-4">{m.role}</div>
                <div className="text-charcoal/75 text-sm leading-relaxed space-y-3">
                  {m.bio.split(/\n\n+/).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance / about company */}
      <section className="bg-base py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="label text-heritage mb-3 flex items-center gap-2"><ClipboardList size={14}/> Governance & history</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-8">Built on 35 years of destination experience.</h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6 text-charcoal/80 leading-relaxed">
            <div>
              <h3 className="font-display text-xl mb-2">Company history</h3>
              <p className="text-sm">
                LPL Tourism evolved from the organisation that managed strategic and operational tourism in
                Leicester and Leicestershire since <strong>1989</strong> (originally trading as
                Leicester Promotions Ltd). Today the company supports a growing range of destinations
                across the UK.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl mb-2">Structure</h3>
              <p className="text-sm">
                Private limited company, limited by guarantee. Funded through commercial agreements and
                project-related work with the public and private sectors.
              </p>
              <p className="text-sm mt-3">
                Registered company no: <strong>02785317</strong>
              </p>
            </div>
            <div className="md:col-span-2">
              <h3 className="font-display text-xl mb-2">Registered office</h3>
              <address className="not-italic text-sm">
                LPL Tourism Ltd · 2 High Street · Loughborough · Leicestershire · LE11 2PY · United Kingdom
              </address>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-charcoal text-base py-20 md:py-24">
        <div className="max-w-[860px] mx-auto px-6 lg:px-12 text-center">
          <BarChart3 className="text-mustard mx-auto mb-4" size={32}/>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
            Interested in working with us?
          </h2>
          <p className="text-base/80 text-lg mb-8 max-w-2xl mx-auto">
            Whether it&apos;s a specific project, a retained advisory arrangement, or an initial scoping call —
            drop Martin a line and we&apos;ll come back within a couple of working days.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="mailto:martin.peters@l-p-l.com?subject=Consultancy%20enquiry"
              data-testid="services-cta-email-martin"
              className="inline-flex items-center gap-2 bg-mustard text-charcoal px-6 py-3 rounded-full font-semibold hover:bg-base transition-colors">
              <Mail size={14}/> Email Martin Peters
            </a>
            <Link to="/about" data-testid="services-cta-about"
              className="inline-flex items-center gap-2 border border-base/40 px-6 py-3 rounded-full font-semibold hover:bg-base/10 transition-colors">
              About the wider team <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
